/**
 * cnc-reviews — the fetched half of the product reviews section.
 *
 * The star average, the review count and the structured data are server-rendered
 * from product metafields and are already correct when this file runs. Everything
 * here is the part Liquid cannot do: the individual reviews (Liquid caps
 * metaobject iteration at 50 entries store-wide), the per-star histogram, the
 * photo strip, and writing.
 *
 * ONE PROBE DECIDES. /apps/cnc/product-reviews is an App Proxy route that may not
 * be deployed. Rather than assume, the first GET is the capability check: until it
 * answers the list stays hidden and the section is carried by the summary alone.
 * A list that 404s is worse than no list — the same rule the account page follows.
 *
 * Shopify signs the proxy request itself, appending `logged_in_customer_id` from
 * its own session cookie, so this file never sends a customer id and could not be
 * trusted if it did.
 */
(function () {
  "use strict";

  var ENDPOINT = "/apps/cnc/product-reviews";
  var BOOTED   = "cncReviewsBooted";

  // ── small helpers ──────────────────────────────────────────────────────────
  function $(root, sel) { return root.querySelector(sel); }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /**
   * The same star the rest of the page draws — `cnc-icon`'s path, inlined
   * because a snippet can't be rendered from JS. Filled vs empty is a colour on
   * the wrapper, never a different shape, so the row never looks ragged.
   *
   * These used to be plain <span>s coloured by CSS; when the mini stars moved
   * off squares onto the shared treatment they rendered as nothing at all.
   * Emitting the glyph here keeps the markup self-sufficient.
   */
  var STAR_PATH = "m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2Z";

  function starsHTML(rating) {
    var out = "";
    for (var i = 1; i <= 5; i++) {
      out += '<svg class="' + (i > rating ? "off" : "") + '" width="13" height="13" viewBox="0 0 24 24" ' +
             'fill="currentColor" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    }
    return '<span class="cnc-rv-mini" role="img" aria-label="' + rating + ' out of 5">' + out + "</span>";
  }

  /**
   * "2 days ago" beats an ISO string, and an absolute date beats a relative one
   * once it stops being recent — nobody reads "412 days ago" as a quantity.
   */
  function when(iso) {
    if (!iso) return "";
    var t = Date.parse(iso);
    if (isNaN(t)) return "";
    var days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return days + " days ago";
    return new Date(t).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }

  // ── rendering ──────────────────────────────────────────────────────────────

  function renderBars(root, summary) {
    var box = $(root, "[data-cnc-rv-bars]");
    if (!box || !summary || !summary.count) return;
    var html = "";
    for (var star = 5; star >= 1; star--) {
      var n   = (summary.histogram && summary.histogram[star]) || 0;
      var pct = summary.count ? Math.round((n / summary.count) * 100) : 0;
      html +=
        '<div class="cnc-rv-bar">' +
          '<span class="cnc-rv-bar-n">' + star +
            '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="' + STAR_PATH + '"/></svg></span>' +
          '<span class="cnc-rv-bar-track"><i style="width:' + pct + '%"></i></span>' +
          '<span class="cnc-rv-bar-c">' + n + "</span>" +
        "</div>";
    }
    box.innerHTML = html;
    show(box);
  }

  function renderPhotos(root, reviews) {
    var box   = $(root, "[data-cnc-rv-photos]");
    var strip = $(root, "[data-cnc-rv-photos-strip]");
    if (!box || !strip) return;

    var shots = [];
    reviews.forEach(function (r) {
      (r.photos || []).forEach(function (p) {
        if (p && p.url) shots.push({ url: p.url, alt: p.alt || "", author: r.author });
      });
    });
    if (!shots.length) return;

    strip.innerHTML = shots.slice(0, 12).map(function (s) {
      // Ask the CDN for a thumbnail rather than shipping a 4000px original.
      var thumb = s.url + (s.url.indexOf("?") > -1 ? "&" : "?") + "width=240";
      return '<img class="cnc-rv-photo" src="' + esc(thumb) + '" loading="lazy" ' +
             'alt="' + esc(s.alt || ("Review photo from " + s.author)) + '">';
    }).join("");
    show(box);
  }

  /* Is there anything to READ here?

     A rating with no words and no photos is a vote, not a review. It belongs in
     the average, the count and the histogram — all of which come from the
     server aggregate and go on counting it — but as its own card it was a row
     that said nothing: five stars, a name, a date, no reason. A handful of them
     push the one written review off the screen, which is the opposite of what
     the section is for.

     Photos count as substance. Someone who posts a picture of the toy in use
     and no caption has said plenty. */
  function hasSubstance(r) {
    return !!((r.body && r.body.trim()) || (r.photos && r.photos.length));
  }

  /* The "rated, not written about" line. Rendered by Liquid and toggled here
     rather than built in JS, so its wording is a section setting the shop can
     edit like every other string in this section. */
  function toggleNoWords(root, on) {
    var el = $(root, "[data-cnc-rv-nowords]");
    if (!el) return;
    if (on) show(el); else hide(el);
  }

  function reviewHTML(r) {
    var name  = esc(r.author || "CNC Pet Parent");
    var badge = r.verified
      ? '<span class="cnc-rv-verified">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
          'stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M20 6 9 17l-5-5"/></svg>Verified</span>'
      : "";
    var body  = r.body ? '<p class="cnc-rv-body">' + esc(r.body) + "</p>" : "";
    var shots = (r.photos || []).length
      ? '<div class="cnc-rv-item-photos">' + r.photos.slice(0, 4).map(function (p) {
          var t = p.url + (p.url.indexOf("?") > -1 ? "&" : "?") + "width=200";
          return '<img src="' + esc(t) + '" loading="lazy" alt="' + esc(p.alt || ("Photo from " + name)) + '">';
        }).join("") + "</div>"
      : "";

    return (
      '<article class="cnc-rv-item' + (r.body ? '' : ' cnc-rv-item--bare') + '">' +
        '<div class="cnc-rv-item-head">' +
          starsHTML(r.rating) +
          '<span class="cnc-rv-author">' + name + "</span>" +
          badge +
          '<span class="cnc-rv-when">' + esc(when(r.createdAt)) + "</span>" +
        "</div>" +
        body + shots +
      "</article>"
    );
  }

  // ── controller ─────────────────────────────────────────────────────────────

  function init(root) {
    if (root.dataset[BOOTED]) return;
    root.dataset[BOOTED] = "1";

    var productId = root.getAttribute("data-product-id");
    if (!productId) return;

    var listEl = $(root, "[data-cnc-rv-list]");
    var skelEl = $(root, "[data-cnc-rv-skeleton]");
    var moreEl = $(root, "[data-cnc-rv-more]");
    var emptyEl = $(root, "[data-cnc-rv-empty]");
    var writeEl = $(root, "[data-cnc-rv-write]");

    var page = 0;
    var all  = [];

    function load(next) {
      return fetch(ENDPOINT + "?productId=" + encodeURIComponent(productId) + "&page=" + next, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
        .then(function (r) {
          // Anything other than a clean JSON 200 means "not available here".
          if (!r.ok) throw new Error("proxy " + r.status);
          var ct = r.headers.get("content-type") || "";
          if (ct.indexOf("application/json") === -1) throw new Error("proxy returned " + ct);
          return r.json();
        });
    }

    load(0)
      .then(function (data) {
        hide(skelEl);
        all = data.reviews || [];

        if (!all.length) {
          // No approved reviews. Liquid already rendered the empty state and
          // its CTA is the write button — nothing to unhide here. Unhiding the
          // standalone button too is what produced two identical buttons.
          return;
        }

        hide(emptyEl);

        /* `all` stays whole — the photo strip reads it, and the bars come from
           the server's own summary — while only the LIST is narrowed to the
           reviews that have something to read. */
        var readable = all.filter(hasSubstance);
        listEl.innerHTML = readable.map(reviewHTML).join("");
        if (readable.length) show(listEl);

        /* Ratings, but nobody has written anything yet. Without this the panel
           showed "5.0 from 3 pet parents who bought it" above a blank space,
           which reads as a list that failed to load rather than one that has
           nothing in it. */
        toggleNoWords(root, readable.length === 0);

        renderBars(root, data.summary);
        renderPhotos(root, all);
        if (data.hasMore) show(moreEl);
      })
      .catch(function (err) {
        // Not deployed, or briefly down. Both look the same to a shopper and
        // both should look like "no list here", never like a broken page.
        hide(skelEl);
        hide(listEl);
        hide(moreEl);
        if (window.console) console.info("[cnc-reviews] list unavailable:", err.message);
      });

    if (moreEl) {
      moreEl.addEventListener("click", function () {
        moreEl.disabled = true;
        load(++page)
          .then(function (data) {
            var rows = data.reviews || [];
            all = all.concat(rows);

            /* A page can be all ratings and no words, in which case this click
               appends nothing. Keep the button alive so the next page is still
               reachable rather than leaving the shopper on a button that
               visibly did nothing and then vanished. */
            var readable = rows.filter(hasSubstance);
            if (readable.length) {
              listEl.insertAdjacentHTML("beforeend", readable.map(reviewHTML).join(""));
              show(listEl);
              toggleNoWords(root, false);
            }

            renderPhotos(root, all);
            moreEl.disabled = false;
            if (!data.hasMore) hide(moreEl);
          })
          .catch(function () { hide(moreEl); });
      });
    }

    wireForm(root, productId);
  }

  /* ── the won card ─────────────────────────────────────────────────────────
     What the app shows after a review, on the web: the review is in, and here
     is what it earned. Replaces the form in place — a modal at the end of a
     task the shopper has just completed is one more thing to dismiss before
     they can see the thing they made.

     THE NUMBER IS THE SERVER'S. `points` comes off the POST response and is
     never computed here: a total this file could work out for itself is a
     total a shopper could edit in devtools and screenshot back at us. No
     points on the response (backend not deployed yet, or the ledger write
     failed) simply means no gold panel — a "+0" in a rewards frame is worse
     than a plain thank-you.

     `pending` is the review's own state, not a hedge. A web review is created
     DRAFT and the points ride with it as a pending ledger entry, so they are
     visible in Rewards → history from this moment and spendable once the
     review is approved. Saying which of the two is true here is what prevents
     "where are my points" tomorrow morning. */
  function celebrate(root, data) {
    var wonEl = $(root, "[data-cnc-rv-won]");
    if (!wonEl) return;

    var prizeEl = $(root, "[data-cnc-rv-won-prize]");
    var ptsEl   = $(root, "[data-cnc-rv-won-pts]");
    var lineEl  = $(root, "[data-cnc-rv-won-line]");

    show(wonEl);
    wonEl.scrollIntoView({ block: "nearest", behavior: "smooth" });

    var points = data && data.points;
    var earned = points ? (parseInt(points.awarded, 10) || 0) : 0;
    if (!earned || !prizeEl || !ptsEl) return;

    var held = points.status === "pending" || data.pending === true;
    if (lineEl) {
      lineEl.textContent = wonEl.getAttribute(held ? "data-hold-note" : "data-live-note") || "";
    }
    show(prizeEl);
    countUp(ptsEl, earned);
  }

  /* 0 → total over ~900ms, eased out. The animation is the point: a number
     simply printed reads as a receipt, one that climbs reads as a win, and this
     is the only moment in the section worth a flourish.

     THE TRUE NUMBER IS WRITTEN FIRST AND THE ANIMATION PAINTS OVER IT — the
     rule cnc-cart.js's saving counter arrived at the hard way. requestAnimation-
     Frame does not run in a backgrounded tab, and a shopper who posts a review
     and switches tabs comes back to whatever the last frame left behind. On the
     cart that was a wrong money figure; here it would be a gold panel headed
     REWARDS EARNED reading "+0" over points that were genuinely credited —
     which is not a stalled animation, it is a lie about a balance.

     So: the total is set before anything is allowed to move, re-asserted on a
     timer that outlives the animation, and zeroed only on the same synchronous
     tick that starts the frames (two writes in one tick paint once, so there is
     no flash of the answer before the count). */
  var COUNT_MS = 900;

  function countUp(el, total) {
    var final = "+" + total;
    el.textContent = final;

    var animate = !document.hidden &&
      typeof requestAnimationFrame === "function" &&
      !(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (!animate) return;

    setTimeout(function () { el.textContent = final; }, COUNT_MS + 500);

    var started = 0;
    function frame(now) {
      if (!started) started = now;
      var t = Math.min(1, (now - started) / COUNT_MS);
      if (t < 1) {
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = "+" + Math.round(eased * total);
        requestAnimationFrame(frame);
      } else {
        el.textContent = final;      // never land on a rounding artefact
      }
    }

    el.textContent = "+0";
    requestAnimationFrame(frame);
  }

  function wireForm(root, productId) {
    var openEl   = $(root, "[data-cnc-rv-open]");
    var formEl   = $(root, "[data-cnc-rv-form]");
    if (!openEl || !formEl) return;                 // signed out — nothing to wire

    var pickEl   = $(root, "[data-cnc-rv-pick]");
    var bodyEl   = $(root, "[data-cnc-rv-body]");
    var submitEl = $(root, "[data-cnc-rv-submit]");
    var cancelEl = $(root, "[data-cnc-rv-cancel]");
    var msgEl    = $(root, "[data-cnc-rv-msg]");
    var stars    = pickEl ? Array.prototype.slice.call(pickEl.querySelectorAll(".cnc-rv-pick-star")) : [];
    var rating   = 0;

    function paint(v) {
      stars.forEach(function (s, i) {
        var on = i < v;
        s.classList.toggle("on", on);
        s.setAttribute("aria-checked", i === v - 1 ? "true" : "false");
      });
      submitEl.disabled = v < 1;
    }

    stars.forEach(function (s) {
      s.addEventListener("click", function () { rating = parseInt(s.dataset.value, 10) || 0; paint(rating); });
      s.addEventListener("mouseenter", function () { paint(parseInt(s.dataset.value, 10) || 0); });
    });
    if (pickEl) pickEl.addEventListener("mouseleave", function () { paint(rating); });

    var emptyEl = $(root, "[data-cnc-rv-empty]");
    var earnEl  = $(root, "[data-cnc-rv-write] .cnc-rv-earn");

    openEl.addEventListener("click", function () {
      hide(openEl); hide(emptyEl); show(formEl);
      if (earnEl) hide(earnEl);          // restated inside the form; not twice
      /* The button now lives in the panel's top-right corner and the form
         opens underneath it. On a phone, and on any product whose summary is
         tall, "underneath" is off-screen — so the control that reveals the
         form is also the one that has to bring it into view. */
      formEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      var first = stars[0]; if (first) first.focus();
    });
    cancelEl.addEventListener("click", function () {
      hide(formEl); hide(msgEl);
      // Put back whichever entry point the shopper came from.
      if (emptyEl) show(emptyEl); else show(openEl);
      if (earnEl && !emptyEl) show(earnEl);
    });

    function say(text, kind) {
      msgEl.textContent = text;
      msgEl.className = "cnc-rv-msg " + (kind || "");
      show(msgEl);
    }

    formEl.addEventListener("submit", function (e) {
      e.preventDefault();
      if (rating < 1) return;

      submitEl.disabled = true;
      submitEl.textContent = "Posting…";

      fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          productId: productId,
          rating: rating,
          body: (bodyEl.value || "").trim(),
          author: root.getAttribute("data-customer-name") || "CNC Pet Parent",
        }),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, j: j }; }); })
        .then(function (res) {
          if (res.ok && res.j.ok) {
            hide(formEl); hide(msgEl);
            celebrate(root, res.j);
            return;
          }
          // The API distinguishes "sign in" from "you didn't buy this", and the
          // difference matters to the person reading it.
          if (res.j && res.j.code === "not_purchased") {
            say("Reviews are open to customers who've bought this one. If you ordered it in the app, review it from your order.", "warn");
          } else if (res.j && res.j.code === "login_required") {
            say("Please sign in again — your session looks expired.", "warn");
          } else {
            say("That didn't post. Try again in a moment.", "warn");
          }
          submitEl.disabled = false;
          submitEl.textContent = "Post review";
        })
        .catch(function () {
          say("That didn't post. Try again in a moment.", "warn");
          submitEl.disabled = false;
          submitEl.textContent = "Post review";
        });
    });

    paint(0);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-cnc-reviews]"), init);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
